import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Typography, 
  IconButton, 
  Box,
  Chip,
  Avatar,
  Divider,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import TagIcon from '@mui/icons-material/Tag';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../components/AuthContext';

const FeedPost = ({ post, onDelete, onHashtagClick }) => {
  const { user } = useAuth();
  const isAuthor = user?.username === post.created_by;
  
  // Format the date
  const formattedDate = post.date ? 
    formatDistanceToNow(new Date(post.date), { addSuffix: true }) : 
    'Unknown time';
  
  // Get author name
  const authorName = post.author_name || post.created_by;
  
  // Prepare avatar initials
  const initials = React.useMemo(() => {
    if (post.author_name) {
      return post.author_name.split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    return post.created_by ? post.created_by.substring(0, 2).toUpperCase() : 'UN';
  }, [post.author_name, post.created_by]);
  
  // Calculate the avatar color
  const avatarColor = React.useMemo(() => 
    stringToColor(post.created_by || ''),
    [post.created_by]
  );
  
  // Function to highlight hashtags in content and make them clickable
  const renderContentWithHighlightedHashtags = (content) => {
    if (!content) return '';
    
    // Split content by hashtag pattern to create array of text and hashtags
    const parts = content.split(/(#\w+)/g);
    
    return parts.map((part, index) => {
      // If this part is a hashtag
      if (part.match(/#\w+/)) {
        const hashtag = part.substring(1); // Remove the # symbol
        
        return (
          <span 
            key={index} 
            className="hashtag" 
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              onHashtagClick && onHashtagClick(hashtag);
            }}
            style={{ 
              cursor: 'pointer', 
              color: 'primary.main', 
              fontWeight: 'medium'
            }}
          >
            {part}
          </span>
        );
      }
      
      // Return regular text
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <Card sx={{ 
      mb: 3, 
      borderRadius: 2,
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)'
    }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: avatarColor }}>
            {initials}
          </Avatar>
        }
        action={
          isAuthor && (
            <IconButton 
              aria-label="delete" 
              onClick={() => onDelete(post.id)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          )
        }
        title={
          <Box display="flex" alignItems="center">
            <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
              {post.title}
            </Typography>
            <Chip 
              label={post.group_id} 
              size="small" 
              sx={{ ml: 1 }}
              color="primary"
              variant="outlined"
            />
          </Box>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Posted by {authorName} • {formattedDate}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Typography 
          variant="body1" 
          sx={{ 
            whiteSpace: 'pre-line',
            mb: post.hashtags?.length > 0 ? 2 : 0,
            '& .hashtag': {
              color: 'primary.main',
              fontWeight: 'medium'
            }
          }}
        >
          {renderContentWithHighlightedHashtags(post.content)}
        </Typography>
        
        {/* Display hashtags if available */}
        {post.hashtags && post.hashtags.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
            {post.hashtags.map((tag, index) => (
              <Chip
                key={index}
                icon={<TagIcon />}
                label={tag}
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => onHashtagClick && onHashtagClick(tag)}
                sx={{ mb: 1, cursor: 'pointer' }}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// Helper function to generate color from string
const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

export default FeedPost;
